import { useState } from "react";
import type {
  Convenio,
  CredencialDoCatalogo,
  ProvisionamentoDoConvenio,
} from "@/api/contrato";
import { useBancos, useProvisionar } from "@/api/consultas";
import { useAvisos } from "@/componentes/Avisos";
import { Campo } from "@/componentes/Campo";
import { Confirmacao } from "@/componentes/Confirmacao";
import { Dica } from "@/componentes/Dica";
import { Esqueleto } from "@/componentes/Esqueleto";
import { ModalDeCadastro } from "@/componentes/ModalDeCadastro";
import { Pilula, PilulaDeExcecao } from "@/componentes/PilulaDeSituacao";
import { useSessao } from "@/sessao/useSessao";
import { bancoDoCatalogo } from "./bancos";
import {
  corpoDoProvisionamento,
  dicaDoEstado,
  dicaDosDadosAlterados,
  errosDoProvisionamento,
  ROTULO_DA_CREDENCIAL,
  rotuloDoEstado,
  textoDaRecusaDoProvisionamento,
  type ValoresDaCredencial,
} from "./provisionamento";
import { textoDaRecusa } from "./recusas";

/*
 * O estado do provisionamento, ao lado do nome do convênio. Estado normal não se anuncia:
 * provisionado sem texto à vista, e a palavra fica para o leitor de tela.
 */
export function SeloDoProvisionamento({
  provisionamento,
}: {
  provisionamento: ProvisionamentoDoConvenio;
}) {
  const { estado, campos_alterados: alterados = [] } = provisionamento;

  return (
    <>
      <span className="flex items-center gap-1">
        <PilulaDeExcecao
          normal={estado === "provisionado"}
          matiz={estado === "falhou" ? "falha" : "transito"}
        >
          {rotuloDoEstado(estado)}
        </PilulaDeExcecao>
        {estado !== "provisionado" && <Dica texto={dicaDoEstado(provisionamento)} />}
      </span>

      {alterados.length > 0 && (
        <span className="flex items-center gap-1">
          <Pilula matiz="transito">dados alterados</Pilula>
          <Dica texto={dicaDosDadosAlterados(alterados)} />
        </span>
      )}
    </>
  );
}

const EFEITO = "Cria o cadastro na TecnoSpeed, em produção, e reaproveita o que já existe lá.";

/*
 * Provisionar, aberto por `?abrir=provisionar`.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A CREDENCIAL SEGUE A REGRA DA SENHA.                                     │
 * │                                                                          │
 * │ Mora nos `useState` daqui, sai do estado antes da viagem e não sobrevive │
 * │ ao modal fechado. A API a repassa ao provider, e não a grava.            │
 * │                                                                          │
 * │ Os campos vêm do catálogo do banco. Sem nenhum, não há o que preencher,  │
 * │ e um modal vazio seria um botão a mais: a tela vai direto à confirmação, │
 * │ que é onde a recusa aparece nesse caso.                                  │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function ProvisionarConvenio({
  convenio,
  aoFechar,
}: {
  convenio: Convenio;
  aoFechar: () => void;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const catalogo = useBancos(cliente, true);
  const provisionar = useProvisionar(cliente, convenio.id);

  const [valores, setValores] = useState<ValoresDaCredencial>({});
  const [erros, setErros] = useState<Partial<Record<CredencialDoCatalogo, string>>>({});
  const [erroGeral, setErroGeral] = useState<string | undefined>(undefined);
  const [confirmando, setConfirmando] = useState(false);

  const nome = `${convenio.banco}/${convenio.numero}`;
  const pronto = catalogo.data !== undefined;
  const erroDeLeitura =
    pronto || catalogo.error === null ? undefined : textoDaRecusa(catalogo.error);
  const credenciais =
    bancoDoCatalogo(catalogo.data?.bancos ?? [], convenio.banco)?.credenciais_webservice ?? [];
  const semCampos = pronto && credenciais.length === 0;

  async function enviar() {
    const pedido = corpoDoProvisionamento(valores);
    setValores({});

    try {
      await provisionar.enviar(pedido);
    } catch (falha) {
      // Sem campos, não há modal por trás: a confirmação mostra a recusa e fica aberta.
      if (semCampos) throw new Error(textoDaRecusaDoProvisionamento(falha), { cause: falha });

      const { porCampo, geral } = errosDoProvisionamento(falha, credenciais);
      setErros(porCampo);
      setErroGeral(geral);
      setConfirmando(false);
      return;
    }

    avisos.feito("Convênio provisionado");
    aoFechar();
  }

  function mudar(credencial: CredencialDoCatalogo, valor: string) {
    setValores((atuais) => ({ ...atuais, [credencial]: valor }));
    setErros(({ [credencial]: _resolvido, ...restantes }) => restantes);
    setErroGeral(undefined);
  }

  const confirmacao = (
    <Confirmacao
      pedido={
        confirmando || semCampos
          ? {
              severidade: "destrutiva",
              titulo: `Provisionar o convênio ${nome}?`,
              corpo: EFEITO,
              verbo: "Provisionar",
            }
          : null
      }
      aoConfirmar={enviar}
      aoCancelar={semCampos ? aoFechar : () => setConfirmando(false)}
    />
  );

  if (semCampos) return confirmacao;

  return (
    <>
      <ModalDeCadastro
        aberto
        aoFechar={aoFechar}
        titulo="Provisionar convênio"
        descricao={nome}
        aoEnviar={
          pronto
            ? () => {
                setErroGeral(undefined);
                setConfirmando(true);
              }
            : () => void catalogo.refetch()
        }
        enviando={provisionar.isPending || (!pronto && erroDeLeitura === undefined)}
        erro={erroDeLeitura ?? erroGeral}
        rotuloDeEnvio={erroDeLeitura === undefined ? "Provisionar" : "Tentar de novo"}
      >
        {!pronto && erroDeLeitura === undefined && <Esqueleto className="h-[60px]" />}

        {credenciais.map((credencial) => (
          <Campo
            key={credencial}
            rotulo={ROTULO_DA_CREDENCIAL[credencial]}
            dica="Só é exigido para criar o convênio lá. Repassado à TecnoSpeed, sem ficar guardado aqui."
            type="password"
            autoComplete="new-password"
            value={valores[credencial] ?? ""}
            onChange={(evento) => mudar(credencial, evento.target.value)}
            erro={erros[credencial]}
          />
        ))}
      </ModalDeCadastro>

      {confirmacao}
    </>
  );
}
